<template>
    <div>
      <div class="row">
        <div class="col-8 mb-4">
          <h1 v-if="isNew && !outcomeGroup.id" class="float-left">
            {{ $t("add-learning-outcomes-group") }}
          </h1>
          <h1 v-else-if="loaded" class="float-left">
            {{ outcomeGroup.title }}
          </h1>
          <div v-else class="skeleton midnight h16 w-half"></div>
        </div>
        <div v-if="$UserSession.hasPriv.call(this, 'CURRICULUM_MANAGE')" class="col-4 mb-4">
          <button
            v-if="editing"
            @click="finishEditing"
            :disabled="!outcomeGroup.title"
            class="btn btn-primary float-right ml-4"
          >
            {{ $t("save") }}
          </button>
          <button
            v-else
            @click="startEditing"
            class="btn btn-primary float-right"
          >
            {{ $t("edit") }}
          </button>
          <button
            v-if="editing && outcomeGroup.id"
            @click="$router.go()"
            class="btn btn-outline-danger float-right"
          >
            {{ $t("cancel") }}
          </button>
        </div>
      </div>
  
      <div v-if="error" class="row">
        <div class="col-12">
          <b-alert variant="danger" show>{{ error }}</b-alert>
        </div>
      </div>
  
      <div class="row">
        <div class="col-12 mb-2">
          <label class="font-bold">{{ $t("title") }}</label>
          <input
            v-if="editing"
            v-model="outcomeGroup.title"
            type="text"
            class="form-control"
            :placeholder="$t('enter-title')"
          />
          <p v-else-if="loaded" class="mb-0">{{ outcomeGroup.title }}</p>
          <div v-else class="skeleton midnight h16 w-half"></div>
        </div>
  
        <div class="col-12 mb-2">
          <label class="font-bold">{{ $t("notes") }}</label>
          <textarea-autosize
            v-if="editing"
            v-model="outcomeGroup.note"
            class="form-control"
            :placeholder="$t('enter-notes')"
          />
          <p v-else-if="loaded" class="mb-0">{{ outcomeGroup.note }}</p>
          <div v-else class="skeleton midnight h16 w-half"></div>
        </div>

        <div class="col-12 mb-2">
          <label class="font-bold">{{ $t("type") }}</label>
          <div v-if="editing">
            <YesNoToggle
              v-model="isExternal"
              :label="$t('type')"
              srOnly
              :yesLabel="$t('external-learning-outcomes')"
              :noLabel="$t('internal-learning-outcomes')"
            />
          </div>
          <p v-else-if="loaded" class="mb-0">
            {{ outcomeGroup.class === "1" ? $t("external-learning-outcomes") : $t("internal-learning-outcomes") }}
          </p>
          <div v-else class="skeleton midnight h16 w-half"></div>
        </div>
      </div>
  
      <div v-if="outcomeGroup.id" class="col-12 mb-4">
        <div v-if="$UserSession.hasPriv.call(this, 'CURRICULUM_MANAGE')" class="clearfix mt-1 mb-2">
          <button
            class="float-right btn btn-primary mr-2"
            @click="showAddLOModal = true"
            :disabled="addingLO"
          >
            <b-icon icon="plus" /> {{ $t("new") }}
          </button>
          <NewItem
            v-if="showAddLOModal"
            endpointRoute="learningoutcome/save"
            :fields="newLOFields"
            @saved="handleLOSaved"
            @close="showAddLOModal = false"
          />
        </div>
  
        <div v-if="loaded" class="list-group">
          <Table
            :loading="!loaded"
            :submitting="submitting"
            :cols="[
              { key: 'code',  heading: $t('code'),  size: 2 },
              { key: 'title', heading: $t('title'), size: 4 },
              { key: 'note',  heading: $t('notes'), size: 6 }
            ]"
            :data="learningoutcomes"
            breakpoint="md"
            :filters="{ ...filters, page: filters.page, page_size: filters.page_size }"
            :searchable="true"
            :scrollPaginate="true"
            @refresh="refresh"
            @toPage="toPage"
            @selectItem="item => $router.push(`/curriculum-mapping/learning-outcomes/edit/${item.id}`)"
            :allowRightClick="true"
            @deleteItem="deleteOutcome"
          />
        </div>
      </div>
    </div>
</template>
  
<script>
  import Table from "src/components/Table/Table.vue";
  import NewItem from "src/components/modals/NewItem.vue";
  import YesNoToggle from "src/components/Inputs/YesNoToggle.vue";
  import LearningOutcomes from "src/modules/learningoutcomes.js";
  import CurriculumMap from "src/modules/curriculummap.js";

  export default {
    components: { Table, NewItem, YesNoToggle },
    data() {
      return {
        error: null,
        loaded: false,
        editing: false,
        outcomeGroup: {},
        learningoutcomes: [],
        submitting: false,
        onlyunmapped: false,
        curriculum: null,
        curricula: [],
        filters: { root_id: null, search: null, page_size: 30 },
  
        showAddLOModal: false,
        addingLO: false,
        newLOFields: [
          { id: "id",                     label: null,   value: "" },
          { id: "learningoutcomegroup_id", label: null,   value: this.$route.params.group_id },
          { id: "code",                   label: "code",  value: "", mandatory: true },
          { id: "title",                  label: "title", value: "", mandatory: true },
          { id: "note",                   label: "notes", value: "" }
        ],
  
        editingOutcome: {},
        isNewOutcome: false
      };
    },
    mounted() {
      this.loService = new LearningOutcomes();
      this.cmService = new CurriculumMap();
  
      this.getGroup();
      this.listOutcomesByGroupId();
  
      this.cmService.getNodes({ just_roots: true }).then(res => (this.curricula = res));
  
      if (this.isNew) {
        this.loaded = true;
        this.outcomeGroup.class = "0";
        this.startEditing();
      }
    },
    methods: {
      getGroup() {
        this.loService.getGroup(this.$route.params.group_id)
          .then(res => (this.outcomeGroup = res))
          .finally(() => (this.loaded = true));
      },
      listOutcomesByGroupId() {
        this.submitting = true;
        this.loService.list({
          group_id: this.$route.params.group_id,
          unmapped: this.onlyunmapped,
          ...this.filters
        })
        .then(res => (this.learningoutcomes = res.results))
        .finally(() => (this.submitting = false));
      },
      startEditing() {
        this.editing = true;
      },
      finishEditing() {
        this.$store.commit("showBlockingSpinner");
    this.loService.saveGroup(this.outcomeGroup)
          .then(() => {
            this.editing = false;
            this.getGroup();
          })
          .finally(() => this.$store.commit("hideBlockingSpinner"));
      },
      deleteOutcome(item) {
        this.$store.commit("showBlockingSpinner");
        this.loService.delete(item.id)
          .then(() => this.listOutcomesByGroupId())
          .finally(() => this.$store.commit("hideBlockingSpinner"));
      },
      toggleUnMapped() {
        this.onlyunmapped = !this.onlyunmapped;
        this.listOutcomesByGroupId();
      },
      refresh(filters) {
        this.filters = { ...this.filters, ...filters };
        this.listOutcomesByGroupId();
      },
      toPage(page) {
        this.filters.page = page;
        this.listOutcomesByGroupId();
      },
      handleLOSaved() {
        this.showAddLOModal = false;
        this.listOutcomesByGroupId();
      },
      addNewLearningOutcome() {
        const newLO = {
          id: "",
          learningoutcomegroup_id: this.outcomeGroup.id,
          code: "",
          title: "",
          note: ""
        };
        this.learningoutcomes.unshift(newLO);
        this.$set(this.editingOutcome, "", true);
        this.isNewOutcome = true;
      }
    },
    computed: {
      isNew() {
        return !this.$route.params.group_id;
      },
      isExternal: {
        get() {
          return this.outcomeGroup.class === "1";
        },
        set(val) {
          this.$set(this.outcomeGroup, "class", val ? "1" : "0");
        }
      }
    }
  };
</script>
  